// ────────────────────────────────────────────────────────────────────────────
// Canonical date / timestamp formatters for the entire app.
//
// USE THESE — never write raw `new Date(x).toLocaleString()` calls elsewhere.
// The helpers below:
//   • Accept null / undefined / '' / Date / string — never throw.
//   • Treat naive ISO strings (no Z, no offset) as UTC (matches backend
//     serialize_doc contract: date-only → "YYYY-MM-DD"; timestamps → "...Z").
//   • Use 'en-IN' locale uniformly so every user sees the same formatting.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Internal: coerce any input into a Date object.
 * Returns null for invalid / empty input.
 * Treats naive ISO strings as UTC (correct for our BSON-naive backend).
 */
const _toDate = (input) => {
  if (input === null || input === undefined || input === '') return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input !== 'string') return null;

  const s = input.trim();
  if (!s) return null;

  // Date-only "YYYY-MM-DD" — treat as local midnight to avoid tz shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  // ISO with explicit tz → browser handles natively
  // Naive ISO (no Z, no offset) → append Z so it's interpreted as UTC
  const hasTZ = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTZ ? s : s + 'Z');
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Format a date-only value (due_date, invoice_date, etc.) as "23 Apr 2026".
 * Does NOT show time. Use formatDateTime() if you need time.
 */
export const formatDate = (input) => {
  const d = _toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Short date, no year — "23 Apr". Use in compact tables/cards.
 */
export const formatDateShort = (input) => {
  const d = _toDate(input);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

/**
 * Format a full timestamp — "23 Apr 2026, 2:15 PM".
 * Use for created_at, updated_at, recorded_at, last_login, message.timestamp, etc.
 */
export const formatDateTime = (input) => {
  const d = _toDate(input);
  if (!d) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format time-only — "2:15 PM". For chat bubbles, log entries where date is
 * shown separately in a group header.
 */
export const formatTime = (input) => {
  const d = _toDate(input);
  if (!d) return '';
  return d.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Relative time — "just now" / "5 min ago" / "2 hours ago" / "3 days ago".
 * Caps at 6 days; falls back to formatDate() beyond that.
 * Single source of truth — replaces 5+ ad-hoc implementations across the app.
 */
export const formatRelativeTime = (input) => {
  const d = _toDate(input);
  if (!d) return '';
  const now = new Date();
  const deltaSec = Math.floor((now - d) / 1000);

  if (deltaSec < 0) {
    // Future dates — "in 3 days", "in 2 hours"
    const abs = Math.abs(deltaSec);
    if (abs < 60) return 'in a moment';
    if (abs < 3600) return `in ${Math.floor(abs / 60)} min`;
    if (abs < 86400) return `in ${Math.floor(abs / 3600)} hr`;
    if (abs < 604800) return `in ${Math.floor(abs / 86400)} day${Math.floor(abs / 86400) > 1 ? 's' : ''}`;
    return formatDate(d);
  }

  if (deltaSec < 30) return 'just now';
  if (deltaSec < 60) return `${deltaSec} sec ago`;
  if (deltaSec < 3600) {
    const m = Math.floor(deltaSec / 60);
    return `${m} min ago`;
  }
  if (deltaSec < 86400) {
    const h = Math.floor(deltaSec / 3600);
    return `${h} hour${h > 1 ? 's' : ''} ago`;
  }
  if (deltaSec < 604800) {
    const days = Math.floor(deltaSec / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  return formatDate(d);
};

/**
 * Format a date range — "23–25 Apr 2026" or "23 Apr – 2 May 2026".
 * Collapses same month / same year for compact display.
 */
export const formatDateRange = (start, end) => {
  const s = _toDate(start);
  const e = _toDate(end);
  if (!s && !e) return '';
  if (s && !e) return formatDate(s);
  if (!s && e) return formatDate(e);

  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  const sameDay = sameMonth && s.getDate() === e.getDate();

  if (sameDay) return formatDate(s);
  if (sameMonth) {
    return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  if (sameYear) {
    const sPart = s.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const ePart = e.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${sPart} – ${ePart}`;
  }
  return `${formatDate(s)} – ${formatDate(e)}`;
};

// ────────────────────────────────────────────────────────────────────────────
// Legacy helpers (keep for backward compat with existing calendar code)
// ────────────────────────────────────────────────────────────────────────────

/** Format a Date → "YYYY-MM-DD" in local timezone (for calendar grid math). */
export const formatDateToLocal = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Parse "YYYY-MM-DD" → Date in local tz (avoids UTC off-by-one for calendar). */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  if (dateString.length === 10 && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return _toDate(dateString);
};

export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = date1 instanceof Date ? date1 : parseLocalDate(date1);
  const d2 = date2 instanceof Date ? date2 : parseLocalDate(date2);
  if (!d1 || !d2) return false;
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

export const getTodayLocal = () => formatDateToLocal(new Date());

export const getTomorrowLocal = () => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return formatDateToLocal(t);
};

export const isToday = (dateString) => {
  if (!dateString) return false;
  return dateString === getTodayLocal();
};

export const isPastDate = (dateString) => {
  if (!dateString) return false;
  const date = parseLocalDate(dateString);
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

/** Alias kept for backward compat — prefer `formatDate()` going forward. */
export const formatDisplayDate = formatDate;

/** Alias kept for backward compat — prefer `formatDate()` going forward. */
export const formatTimestampAsDate = formatDate;

export default {
  // Canonical
  formatDate,
  formatDateShort,
  formatDateTime,
  formatTime,
  formatRelativeTime,
  formatDateRange,
  // Legacy (still exported)
  formatDateToLocal,
  parseLocalDate,
  isSameDay,
  getTodayLocal,
  getTomorrowLocal,
  isToday,
  isPastDate,
  formatDisplayDate,
  formatTimestampAsDate,
};
